import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Tooltip, Stack, Chip, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/pl';
import 'dayjs/locale/en';
import api from '../api';

/**
 * UsageCalendar Component
 * Displays a calendar heatmap showing device usage patterns
 * - Green shades: No stress/balanced usage
 * - Yellow/Orange: Intensive stress
 * - Red: Dangerous stress levels
 * - Shows warnings for days with abnormally high usage
 */
function UsageCalendar({ deviceId, year: initialYear, month: initialMonth }) {
  const [calendarData, setCalendarData] = useState([]);
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  
  // Set dayjs locale immediately based on current language
  dayjs.locale(i18n.language);
  
  const [currentYear, setCurrentYear] = useState(initialYear || dayjs().year());
  const [currentMonth, setCurrentMonth] = useState(initialMonth || dayjs().month() + 1);

  const fetchCalendarData = React.useCallback(async () => {
    if (!deviceId) return;
    
    try {
      const response = await api.get(
        `/api/device/${deviceId}/calendar/`,
        { params: { year: currentYear, month: currentMonth } }
      );
      setCalendarData(response.data.calendar || []);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      setCalendarData([]);
    }
  }, [deviceId, currentYear, currentMonth]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Get the color based on stress level using same logic as heatmap
  const getColorForStress = (stressLevel, stressBreakdown, isAlert) => {
    if (stressLevel === 0) {
      return theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5'; // No data
    }

    const total = stressBreakdown.no_stress + stressBreakdown.intensive + stressBreakdown.dangerous;
    if (total === 0) {
      return theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5';
    }

    // Determine base color by category (matching line chart colors)
    let baseColor;
    if (stressLevel <= 5) {
      baseColor = '#4caf50'; // Green (balanced)
    } else if (stressLevel <= 52.75) {
      baseColor = '#fbc02d'; // Yellow (intensive)
    } else {
      baseColor = '#f44336'; // Red (dangerous)
    }

    // Calculate opacity based on progress through the range
    let opacity;
    if (stressLevel <= 5) {
      // 0-5%: Green range
      opacity = 0.5 + (stressLevel / 5) * 0.5; // 50% to 100%
    } else if (stressLevel <= 52.75) {
      // 5-52.75%: Yellow range
      opacity = 0.5 + ((stressLevel - 5) / 47.75) * 0.5;
    } else {
      // 52.75-100%: Red range
      opacity = 0.5 + ((stressLevel - 52.75) / 47.25) * 0.5;
    }

    // Parse hex color to rgb
    const hex = baseColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // If it's an alert, make it more opaque
    if (isAlert) {
      opacity = Math.min(1, opacity * 1.2);
    }

    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Navigate months
  const handlePreviousMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    const today = dayjs();
    const nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;
    const nextYear = currentMonth === 12 ? currentYear + 1 : currentYear;
    
    // Don't allow navigation beyond current month
    if (nextYear > today.year() || (nextYear === today.year() && nextMonth > today.month() + 1)) {
      return;
    }
    
    setCurrentMonth(nextMonth);
    if (currentMonth === 12) {
      setCurrentYear(nextYear);
    }
  };

  // Generate calendar grid
  const generateCalendarGrid = () => {
    const startOfMonth = dayjs().year(currentYear).month(currentMonth - 1).startOf('month');
    const endOfMonth = dayjs().year(currentYear).month(currentMonth - 1).endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');

    const calendar = [];
    let currentDate = startDate;
    let week = [];

    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      const currentDateStr = currentDate.format('YYYY-MM-DD');
      const dayData = calendarData.find(
        (d) => d.date === currentDateStr
      ) || {
        date: currentDateStr,
        total_seconds: 0,
        stress_level: 0,
        is_alert: false,
        stress_breakdown: { no_stress: 0, intensive: 0, dangerous: 0 }
      };

      week.push({
        ...dayData,
        day: currentDate.date(),
        isCurrentMonth: currentDate.month() === currentMonth - 1,
        isToday: currentDate.isSame(dayjs(), 'day'),
      });

      if (week.length === 7) {
        calendar.push(week);
        week = [];
      }

      currentDate = currentDate.add(1, 'day');
    }

    if (week.length > 0) {
      calendar.push(week);
    }

    return calendar;
  };

  const calendarGrid = generateCalendarGrid();
  
  // Get localized day names
  const weekDays = React.useMemo(() => {
    const days = [];
    // Start from Sunday (0) through Saturday (6)
    for (let i = 0; i < 7; i++) {
      days.push(dayjs().day(i).format('ddd'));
    }
    return days;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: '16px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
      }}
    >
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                borderLeft: '4px solid #dc3545',
                paddingLeft: 2,
              }}
            >
              {t('usage_calendar.title')}
            </Typography>
            
            {/* Month/Year Navigation */}
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton 
                size="small" 
                onClick={handlePreviousMonth}
                sx={{ 
                  bgcolor: theme.palette.background.paper,
                  '&:hover': { bgcolor: theme.palette.action.hover }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ minWidth: 150, textAlign: 'center', textTransform: 'capitalize' }}>
                {dayjs().year(currentYear).month(currentMonth - 1).format('MMMM YYYY')}
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleNextMonth}
                disabled={
                  currentYear === dayjs().year() && currentMonth === dayjs().month() + 1
                }
                sx={{ 
                  bgcolor: theme.palette.background.paper,
                  '&:hover': { bgcolor: theme.palette.action.hover }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>
          </Stack>
          
          {/* Legend */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: 'rgba(76, 175, 80, 0.7)',
                  borderRadius: '4px',
                }}
              />
              <Typography variant="caption">{t('usage_calendar.balanced')}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: 'rgba(251, 192, 45, 0.7)',
                  borderRadius: '4px',
                }}
              />
              <Typography variant="caption">{t('usage_calendar.intensive')}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  bgcolor: 'rgba(244, 67, 54, 0.7)',
                  borderRadius: '4px',
                }}
              />
              <Typography variant="caption">{t('usage_calendar.dangerous')}</Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Calendar Grid */}
        <Box>
          {/* Week day headers */}
          <Stack direction="row" mb={1}>
            {weekDays.map((day) => (
              <Box
                key={day}
                sx={{
                  flex: 1,
                  textAlign: 'center',
                  py: 1,
                }}
              >
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  {day}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Calendar days */}
          {calendarGrid.map((week, weekIndex) => (
            <Stack key={weekIndex} direction="row" spacing={0.5} mb={0.5}>
              {week.map((dayData, dayIndex) => {
                const hasData = dayData.total_seconds > 0;
                const color = hasData
                  ? getColorForStress(dayData.stress_level, dayData.stress_breakdown, dayData.is_alert)
                  : theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5';

                return (
                  <Tooltip
                    key={dayIndex}
                    title={
                      hasData ? (
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight="bold">
                            {dayData.date}
                          </Typography>
                          <Typography variant="caption">
                            {t('usage_calendar.total_time')}: {formatTime(dayData.total_seconds)}
                          </Typography>
                          <Typography variant="caption" color="#4caf50">
                            {t('usage_calendar.no_stress')}: {formatTime(dayData.stress_breakdown.no_stress)}
                          </Typography>
                          <Typography variant="caption" color="#fbc02d">
                            {t('usage_calendar.intensive')}: {formatTime(dayData.stress_breakdown.intensive)}
                          </Typography>
                          <Typography variant="caption" color="#f44336">
                            {t('usage_calendar.dangerous')}: {formatTime(dayData.stress_breakdown.dangerous)}
                          </Typography>
                          {dayData.is_alert && (
                            <Chip
                              icon={<WarningAmberIcon />}
                              label={t('usage_calendar.high_stress_alert')}
                              color="error"
                              size="small"
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption">
                          {dayData.date} - {t('usage_calendar.no_data')}
                        </Typography>
                      )
                    }
                    arrow
                    placement="top"
                  >
                    <Box
                      sx={{
                        flex: 1,
                        aspectRatio: '1',
                        bgcolor: color,
                        border: dayData.isToday
                          ? `2px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        cursor: hasData ? 'pointer' : 'default',
                        opacity: dayData.isCurrentMonth ? 1 : 0.3,
                        transition: 'transform 0.2s ease, box-shadow 0.2s ease, z-index 0s',
                        '&:hover': hasData
                          ? {
                              transform: 'scale(1.15)',
                              zIndex: 999,
                              boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                            }
                          : {},
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: dayData.isToday ? 'bold' : 'normal',
                          color: dayData.isCurrentMonth
                            ? theme.palette.text.primary
                            : theme.palette.text.disabled,
                        }}
                      >
                        {dayData.day}
                      </Typography>
                      {dayData.is_alert && (
                        <WarningAmberIcon
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            fontSize: 16,
                            color: '#f44336',
                            filter: 'drop-shadow(0px 1px 3px rgba(0,0,0,0.5))',
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            borderRadius: '50%',
                            padding: '2px',
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

export default UsageCalendar;
